import { useState, useLayoutEffect, useEffect, useRef, useCallback } from 'react'
import { HOVER_CHECK_DELAY, POST_DELETION_CHECK_DELAY, EVENTS } from './notificationConstants'

/**
 * Solves UX issue where deleting a notification causes the next one
 * to slide into position, but hover state doesn't activate without mouse movement.
 */
const useNotificationHover = ({ shouldDisappear, notificationId }) => {
  const [isHovered, setIsHovered] = useState(false)
  const elementRef = useRef(null)
  const mousePositionRef = useRef({ x: 0, y: 0 })

  useEffect(() => {
    const handleMouseMove = event => {
      mousePositionRef.current = { x: event.clientX, y: event.clientY }
    }

    document.addEventListener('mousemove', handleMouseMove, { passive: true })
    return () => document.removeEventListener('mousemove', handleMouseMove)
  }, [])

  const checkMouseOverElement = useCallback(() => {
    if (!elementRef.current || shouldDisappear) return false

    const rect = elementRef.current.getBoundingClientRect()
    const { x, y } = mousePositionRef.current

    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
  }, [shouldDisappear])

  useLayoutEffect(() => {
    const detectHover = () => {
      if (checkMouseOverElement()) {
        setIsHovered(true)
      }
    }

    detectHover()
    const timeoutId = setTimeout(detectHover, HOVER_CHECK_DELAY)

    return () => clearTimeout(timeoutId)
  }, [shouldDisappear, notificationId, checkMouseOverElement])

  useEffect(() => {
    const handleNotificationDeleted = () => {
      setTimeout(() => {
        if (checkMouseOverElement()) {
          setIsHovered(true)
        }
      }, POST_DELETION_CHECK_DELAY)
    }

    window.addEventListener(EVENTS.NOTIFICATION_DELETED, handleNotificationDeleted)
    return () => window.removeEventListener(EVENTS.NOTIFICATION_DELETED, handleNotificationDeleted)
  }, [checkMouseOverElement])

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
  }, [])

  return {
    isHovered,
    elementRef,
    handleMouseEnter,
    handleMouseLeave
  }
}

export default useNotificationHover
